import type { ReactNode } from 'react'
import { Text, View } from 'react-native'
import { Image as ExpoImage } from 'expo-image'
import { LinearGradient } from 'expo-linear-gradient'
import { useLocalSearchParams, useRouter } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Building2, ChevronRight, LockKeyhole, ShieldCheck, UserRound } from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { useListingWizardStore } from '@/lib/stores/listing-wizard.store'
import { COLORS, FONTS, GRADIENTS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { RADIUS, SPACING, useSemanticColors } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'

type HostChoiceCardProps = {
  title: string
  body: string
  badge: string
  icon: ReactNode
  enabled: boolean
  testID: string
  accessibilityLabel: string
  onPress?: () => void
}

export function IndividualHostingComingSoon() {
  const { t } = useTranslation()
  const router = useRouter()
  const { vehicleId: searchVehicleId } = useLocalSearchParams<{ vehicleId?: string }>()
  const wizardVehicleId = useListingWizardStore((s) => s.vehicleId)
  const goToAgencySetup = () =>
    router.push('/hosting/organization/setup?source=hosting_gate' as never)
  const resetWizard = useListingWizardStore((s) => s.reset)
  const goToPersonalListing = () => {
    const editVehicleId = searchVehicleId ?? wizardVehicleId
    if (editVehicleId) {
      router.push(`/hosting/new-listing/details?vehicleId=${editVehicleId}` as never)
      return
    }

    resetWizard()
    router.push('/hosting/new-listing/details' as never)
  }

  return (
    <ScreenContainer variant="content">
      <View
        testID="hosting.coming-soon-screen"
        style={{
          flex: 1,
          minHeight: 560,
          justifyContent: 'center',
          padding: SPACING.lg,
          gap: SPACING.lg,
        }}
      >
        <LinearGradient
          colors={GRADIENTS.primarySmooth.colors}
          start={GRADIENTS.primarySmooth.start}
          end={GRADIENTS.primarySmooth.end}
          style={{
            borderRadius: RADIUS.xl,
            padding: SPACING.lg,
            gap: SPACING.lg,
            overflow: 'hidden',
            ...WEB_SHADOWS.md,
          }}
        >
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: SPACING.md,
            }}
          >
            <View
              style={{
                width: 72,
                height: 72,
                borderRadius: RADIUS.lg,
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: COLORS.white,
              }}
            >
              <ExpoImage
                source={require('../../assets/images/logo-stacked.png')}
                style={{ width: 54, height: 54 }}
                contentFit="contain"
                accessibilityLabel={t('common.app_name')}
              />
            </View>
            <View
              style={{
                minHeight: 44,
                borderRadius: RADIUS.full,
                paddingHorizontal: SPACING.md,
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'row',
                gap: SPACING.xs,
                backgroundColor: COLORS.background.card,
                borderWidth: 1,
                borderColor: COLORS.background.cardBorder,
              }}
            >
              <ShieldCheck size={16} color={COLORS.white} strokeWidth={2} />
              <Text
                style={{
                  fontSize: 12,
                  lineHeight: 18,
                  fontFamily: FONTS.bodyBold,
                  color: COLORS.white,
                }}
                numberOfLines={1}
                adjustsFontSizeToFit
              >
                {t('hosting.host_onboarding_badge')}
              </Text>
            </View>
          </View>

          <View style={{ gap: SPACING.sm }}>
            <Text
              style={{
                fontSize: 28,
                lineHeight: 36,
                fontFamily: FONTS.bodyBold,
                color: COLORS.white,
              }}
            >
              {t('hosting.host_onboarding_title')}
            </Text>
            <Text
              style={{
                fontSize: 15,
                lineHeight: 23,
                fontFamily: FONTS.body,
                color: COLORS.text.secondary,
              }}
            >
              {t('hosting.host_onboarding_subtitle')}
            </Text>
          </View>
        </LinearGradient>

        <View style={{ gap: SPACING.md }}>
          <HostChoiceCard
            title={t('hosting.host_choice_agency_title')}
            body={t('hosting.host_choice_agency_body')}
            badge={t('hosting.host_choice_agency_badge')}
            icon={<Building2 size={24} color={COLORS.coral.DEFAULT} strokeWidth={2} />}
            enabled
            onPress={goToAgencySetup}
            testID="hosting.host-choice-agency"
            accessibilityLabel={t('hosting.host_choice_agency_label')}
          />
          <HostChoiceCard
            title={t('hosting.host_choice_natural_title')}
            body={t('hosting.host_choice_natural_body')}
            badge={t('hosting.host_choice_natural_badge')}
            icon={<UserRound size={24} color={COLORS.coral.DEFAULT} strokeWidth={2} />}
            enabled
            onPress={goToPersonalListing}
            testID="hosting.host-choice-natural"
            accessibilityLabel={t('hosting.host_choice_natural_label')}
          />
        </View>
      </View>
    </ScreenContainer>
  )
}

function HostChoiceCard({
  title,
  body,
  badge,
  icon,
  enabled,
  testID,
  accessibilityLabel,
  onPress,
}: HostChoiceCardProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)

  return (
    <Pressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ disabled: !enabled }}
      disabled={!enabled}
      onPress={onPress}
      style={{
        minHeight: 156,
        borderRadius: RADIUS.lg,
        borderWidth: 2,
        borderColor: enabled ? COLORS.coral.DEFAULT : colors.border,
        backgroundColor: enabled ? colors.surfaceRaised : colors.surface,
        padding: SPACING.lg,
        gap: SPACING.md,
        opacity: enabled ? 1 : 0.78,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: SPACING.md }}>
        <View
          style={{
            width: 48,
            height: 48,
            borderRadius: RADIUS.md,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: enabled ? colors.surfaceSubtle : colors.background,
          }}
        >
          {icon}
        </View>

        <View style={{ flex: 1, gap: SPACING.xs }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
            <Text
              style={{
                flex: 1,
                fontSize: 20,
                lineHeight: 26,
                fontFamily: FONTS.bodyBold,
                color: colors.textPrimary,
              }}
            >
              {title}
            </Text>
            {enabled ? (
              <ChevronRight size={22} color={COLORS.coral.DEFAULT} strokeWidth={2} />
            ) : (
              <LockKeyhole size={20} color={colors.textMuted} strokeWidth={2} />
            )}
          </View>
          <Text
            style={{
              fontSize: 14,
              lineHeight: 21,
              fontFamily: FONTS.body,
              color: colors.textSecondary,
            }}
          >
            {body}
          </Text>
        </View>
      </View>

      <View
        style={{
          alignSelf: 'flex-start',
          minHeight: 32,
          borderRadius: RADIUS.full,
          paddingHorizontal: SPACING.md,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: enabled ? COLORS.coral.DEFAULT : colors.surfaceSubtle,
          borderWidth: enabled ? 0 : 1,
          borderColor: colors.border,
        }}
      >
        <Text
          style={{
            fontSize: 12,
            lineHeight: 18,
            fontFamily: FONTS.bodyBold,
            color: enabled ? COLORS.white : colors.textSecondary,
          }}
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {badge}
        </Text>
      </View>
    </Pressable>
  )
}
